export function isEmpty(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  if (Array.isArray(value) || typeof value === 'string') {
    return value.length === 0;
  }
  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value as object).length === 0;
  }
  return false;
}

/** Splits `items` into consecutive slices of at most `chunkSize` elements. */
export function chunk<T>(items: readonly T[], chunkSize: number): T[][] {
  if (!Number.isInteger(chunkSize) || chunkSize < 1) {
    throw new RangeError(`chunk size must be a positive integer, received ${chunkSize}`);
  }
  const chunks: T[][] = [];
  for (let start = 0; start < items.length; start += chunkSize) {
    chunks.push(items.slice(start, start + chunkSize));
  }
  return chunks;
}

type Order = 'asc' | 'desc';

function compareValues<T>(
  av: T[keyof T] | undefined,
  bv: T[keyof T] | undefined,
  order: Order,
): number {
  if (av === bv) {
    return 0;
  }
  if (av === undefined || av === null) {
    return 1;
  }
  if (bv === undefined || bv === null) {
    return -1;
  }
  const cmp = av < bv ? -1 : 1;
  return order === 'asc' ? cmp : -cmp;
}

export function orderBy<T>(items: readonly T[], keys: (keyof T)[], orders: Order[] = []): T[] {
  return [...items].sort((a, b) => {
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key === undefined) {
        continue;
      }
      const order: Order = orders[i] ?? 'asc';
      const cmp = compareValues(a[key], b[key], order);
      if (cmp !== 0) {
        return cmp;
      }
    }
    return 0;
  });
}
